"use client";
import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import { Box, Skeleton, useMediaQuery, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/store";
import { ELibraryFrontApi } from "../../../../api/e-library/e_library_front.api";

type BannerData = {
    id: string | number;
    url_image: string;
    alt?: string;
};

const BannerSlideELibrary: React.FC = () => {
    const base_api = useSelector((state: RootState) => state.api.image_api);
    const [data, set_data] = useState<BannerData[]>([]);
    const [loading, set_loading] = useState(true);

    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
    const isTabletUp = useMediaQuery(theme.breakpoints.up("sm")); 

    const skeletonCount = isDesktop ? 3 : 1;
    const cardHeight = isTabletUp ? 300 : 220; 

    const handleGetBannerSlideData = async () => {
        set_loading(true);
        try {
            const res = await ELibraryFrontApi.listBanner({
                page: 1,
                limit: 5,
            });
            if (res.status < 300) {
                const response: any[] = res?.data?.data?.items || [];
                set_data(response?.map((list) => {
                    return {
                        id: list?.id_library_banner,
                        url_image: `${base_api}/${list?.media_file_path}${list?.media_file_name}`,
                        alt: list?.media_file_name,
                    }
                }));
            }
        } catch (err) {
            console.log(err);
            set_data([]); // กัน null
        } finally {
            set_loading(false);
        }
    };

    useEffect(() => {
        handleGetBannerSlideData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [base_api]);

    if (loading) {
        return (
            <Box className="w-full" sx={{ pb: 4 }}>
                <Box display="flex" gap={2}>
                    {Array.from({ length: skeletonCount }).map((_, i) => (
                        <Box key={i} sx={{ flex: 1 }}>
                            <Skeleton
                                variant="rounded"
                                height={cardHeight}
                                sx={{ borderRadius: 2 }}
                                animation="wave"
                            />
                        </Box>
                    ))}
                </Box>
            </Box>
        );
    }

    if (!data.length) {
        return (
            <Box className="w-full" sx={{ pb: 4 }}>
                <Skeleton
                    variant="rounded"
                    height={cardHeight}
                    sx={{ borderRadius: 2 }}
                    animation={false}
                />
            </Box>
        );
    }

    return (
        <div className="w-full">
            <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={16}
                pagination={{ clickable: true }}
                navigation
                autoplay={{ delay: 4000 }}
                loop
                breakpoints={{
                    320: { slidesPerView: 1 },
                    640: { slidesPerView: 1 },
                    1024: { slidesPerView: 3 },
                }}
                style={{ paddingBottom: "2rem" }}
            >
                {data.map((item) => (
                    <SwiperSlide key={item.id}>
                        <div
                            className="w-full rounded-xl cursor-grab overflow-hidden"
                            style={{ height: cardHeight }}
                        >
                            <img
                                src={item.url_image}
                                alt={item.alt || "banner"}
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                    </SwiperSlide>
                ))}
            </Swiper>
        </div>
    );
};

export default BannerSlideELibrary;
